import { ReactNode, useCallback, useMemo } from 'react'
import { useAccountant, useUserProfile } from 'core/hooks/data/useDataHook'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import BackNavLink from 'core/components/navigation/BackNavLink'
import { useSettings } from 'core/hooks/settings/useSettings'
import usePathSection from 'core/hooks/navigation/usePathSection'
import useComplianceProgram from 'core/hooks/user/useComplianceProgram'
import { IconType } from 'core/components/icons/types'
import Badge from 'core/widgets/side-menu/Badge'
import {
  AccountantsUrl,
  AccountBankCardUrl,
  AccountDirectDepositUrl,
  AccountFavoritePlacesUrl,
  AccountFavoriteTransactionsUrl,
  AccountFavoriteTripsUrl,
  AccountImporterUrl,
  AccountInsuranceUrl,
  AccountPerksUrl,
  AccountPremiumUrl,
  AccountProfileUrl,
  AccountPurposesUrl,
  AccountTaxCenterUrl,
  AccountTeamUrl,
  AccountTrackerUrl,
  AccountVehiclesUrl,
  AccountWorkHoursUrl,
  ComplianceOverviewUrl,
  ComplianceUrl,
  DashboardUrl,
  DataExportsUrl,
  MvrOverviewUrl,
  PayPeriodsUrl,
  PremiumUrl,
  ReportsUrl,
  RoadSmartUrl,
  SchedulesUrl,
  TaxInfoUrl,
  TeamAutomationsUrl,
  TeamCategoriesUrl,
  TeamComplianceUrl,
  TeamDashboardUrl,
  TeamDataExportUrl,
  TeamIntegrationsUrl,
  TeamMembersUrl,
  TeamMergeUrl,
  TeamPlacesUrl,
  TeamPreferencesUrl,
  TeamPremiumUrl,
  TeamProfileUrl,
  TeamProgramDetailUrl,
  TeamProgramPurposesUrl,
  TeamProgramsUrl,
  TeamReportsUrl,
  TeamRolesUrl,
  TeamSettingsPaymentsUrl,
  TeamsFeatureUrl,
  TeamSingleSignOnUrl,
  TeamSummaryUrl,
  TeamsUrl,
  TeamTagsUrl,
  TeamWalletUrl,
  TransactionsUrl,
  TripsUrl
} from 'constants/RouteUrls'
import useUrlTeamId from 'core/hooks/navigation/useUrlTeamId'
import useUrlProgramId from 'core/hooks/navigation/useUrlProgramId'
import { MotusIconType } from 'core/components/motus-icons/motus-types'
import useRoadSmartVideos from 'core/hooks/road-smart/useRoadSmartVideos'
import usePremiumPlans from 'pages/shared/premium/plans/usePremiumPlans'

export interface MenuItemType {
  url: string
  label: string
  icon: `${IconType}` | null
  show: boolean
  tooltip?: ReactNode
  badge?: ReactNode
  alertBadge?: ReactNode
  className?: string | null
  content?: ReactNode
  isLabelOnly?: boolean
  // To allow menu highlight when on a sub route
  subRoutes?: string[]
  isExternal?: boolean
  //Motus
  motusIcon?: `${MotusIconType}`
}

interface UseMenuItemsArgs {
  open: boolean
}

const useMenuItems = ({ open }: UseMenuItemsArgs) => {
  const { data: currentUser } = useUserProfile()
  const { data: team } = useCurrentTeam()
  const { currentPlan } = usePremiumPlans()

  const { data: accountantsInfo } = useAccountant({
    opts: {
      enabled: !!currentUser?.group_as_accountant?.token_id,
      refetchOnWindowFocus: false
    }
  })
  const { section } = usePathSection()
  const {
    data: {
      featureFlags: {
        ENABLE_EVERLANCE_PAYMENTS,
        ENABLE_B2C_2,
        ENABLE_DRIVER_EXPERIENCE: isMotus,
        ENABLE_TAAP2
      }
    }
  } = useSettings()

  const { toDoVideos } = useRoadSmartVideos()

  const {
    hasTeam,
    hasOnlyRole,
    permissions: {
      hasFullPower,
      hasAccessToTeamDashboard,
      hasAccessToComplianceDashboard
    },
    roles: { isReviewer, isSuperAdmin, isRootAdmin }
  } = useRolesAndPermissions()
  const isOnlyReviewer = hasOnlyRole('isReviewer')

  const { bank_integration_enabled, token_id: currentTeamId } = team ?? {}

  const {
    subscription,
    total_uncategorized_transactions_count,
    total_uncategorized_trips_count,
    persona,
    institution_users,
    teams,
    show_tax_report,
    verification_status,
    mvr_completed
  } = currentUser ?? {}

  const program = useComplianceProgram()
  const urlTeamTokenId = useUrlTeamId()
  const urlProgramTokenId = useUrlProgramId()

  const isCurrentTeamRoot = !!teams?.[0]?.is_root

  const showAccountants = useMemo(() => {
    return !!accountantsInfo?.clients
  }, [accountantsInfo?.clients])

  const complianceRequired = useMemo(() => {
    if (ENABLE_TAAP2) {
      return program?.program_type === 'FAVR' || !!program?.verification_needed
    }
    return !!program?.verification_needed
  }, [ENABLE_TAAP2, program?.program_type, program?.verification_needed])

  const showMvr = useMemo(() => {
    return !!currentUser?.teams?.[0]?.mvr_enabled
  }, [currentUser])

  const roadSmartAdminEnabled = currentUser?.road_smart?.enabled === true

  const hasMotusProtectItems = isMotus && (roadSmartAdminEnabled || showMvr)

  const hasSubscription = useMemo(() => {
    return !!subscription?.token_id
  }, [subscription?.token_id])

  const showPersonalReports = useMemo(() => {
    return !!(hasTeam || persona?.includes('Employee'))
  }, [hasTeam, persona])

  const showBankCards = useMemo(() => {
    return !hasTeam || !!bank_integration_enabled || !!institution_users?.length
  }, [bank_integration_enabled, hasTeam, institution_users?.length])

  const motusComplianceBadgeCount = useMemo(() => {
    const _verificationCount =
      verification_status?.toLocaleLowerCase() === 'unsubmitted' ||
      verification_status?.toLocaleLowerCase() === 'rejected'
        ? 1
        : 0

    const _mvrCount = mvr_completed ? 0 : 1

    return _verificationCount + _mvrCount
  }, [mvr_completed, verification_status])

  /* Items shown on personal dashboard */
  const DashboardItems = useMemo((): Record<string, MenuItemType> => {
    return {
      home: {
        url: DashboardUrl,
        label: 'Home',
        icon: 'home',
        motusIcon: 'home',
        show: true
      },
      premium: {
        url: PremiumUrl,
        label: ENABLE_B2C_2 ? 'Explore Plans' : 'Unlock Premium',
        icon: 'premium',
        show: !hasTeam && !hasSubscription
      },
      trips: {
        url: TripsUrl,
        label: 'Trips',
        icon: 'car',
        motusIcon: 'car',
        show: true,
        tooltip: total_uncategorized_trips_count ? (
          <div className="flex items-center w-full gap-2">
            <span>Trips</span>
            <Badge>{total_uncategorized_trips_count}</Badge>
          </div>
        ) : null,
        badge: total_uncategorized_trips_count
          ? total_uncategorized_trips_count
          : null
      },
      transactions: {
        url: TransactionsUrl,
        label: 'Transactions',
        icon: 'transaction',
        show: !isMotus,
        tooltip: total_uncategorized_transactions_count ? (
          <div className="flex items-center w-full gap-2">
            <span>Transactions</span>
            <Badge>{total_uncategorized_transactions_count}</Badge>
          </div>
        ) : null,
        badge: total_uncategorized_transactions_count
          ? total_uncategorized_transactions_count
          : null
      },
      reports: {
        url: ReportsUrl,
        label: 'Reports',
        icon: 'report',
        motusIcon: 'report',
        show: showPersonalReports
      },
      compliance: {
        url: ComplianceOverviewUrl,
        label: 'Compliance',
        icon: 'clipboard',
        motusIcon: 'clipboard',
        show: complianceRequired && !isReviewer,
        alertBadge: (isMotus && motusComplianceBadgeCount) || null,
        isExternal: isMotus,
        subRoutes: [ComplianceUrl]
      },
      mvr: {
        url: MvrOverviewUrl,
        label: 'MVR',
        icon: 'clipboard',
        motusIcon: 'clipboard',
        show: isMotus && showMvr,
        alertBadge: isMotus && !mvr_completed ? 1 : null,
        isExternal: isMotus
      },
      roadSmart: {
        url: RoadSmartUrl,
        label: 'Road Smart',
        icon: null,
        motusIcon: 'clapperboard',
        alertBadge: toDoVideos.length || null,
        show: isMotus && roadSmartAdminEnabled
      },
      taxInfo: {
        url: TaxInfoUrl,
        label: 'Taxes',
        icon: 'tax',
        show: !!show_tax_report && !isMotus
      },
      dataExports: {
        url: DataExportsUrl,
        label: 'Data exports',
        icon: 'download-excel',
        motusIcon: 'csv',
        show: true
      },
      setting: {
        url: isMotus ? AccountWorkHoursUrl : AccountProfileUrl,
        label: isMotus ? 'Menu' : 'Settings',
        icon: 'gear',
        motusIcon: 'menu',
        show: true
      },
      teamDashboard: {
        url: TeamDashboardUrl,
        label: 'Team Dashboard',
        icon: 'team',
        show: hasAccessToTeamDashboard && !isOnlyReviewer
      },
      accountants: {
        url: AccountantsUrl,
        label: 'Accountants',
        icon: 'tax',
        show: showAccountants
      },
      teamFeatures: {
        url: TeamsFeatureUrl,
        label: 'Team Dashboard',
        icon: 'team',
        show: !hasTeam
      },
      teamCompliance: {
        url: TeamComplianceUrl,
        label: 'Compliance Review',
        icon: 'clipboard',
        show: isReviewer
      }
    }
  }, [
    ENABLE_B2C_2,
    complianceRequired,
    hasAccessToTeamDashboard,
    hasSubscription,
    hasTeam,
    isMotus,
    isOnlyReviewer,
    isReviewer,
    motusComplianceBadgeCount,
    mvr_completed,
    showAccountants,
    showPersonalReports,
    showMvr,
    roadSmartAdminEnabled,
    show_tax_report,
    total_uncategorized_transactions_count,
    total_uncategorized_trips_count,
    toDoVideos.length
  ])

  /* Items shown on personal settings */
  const PersonalSettingsItems = useMemo((): Record<string, MenuItemType> => {
    return {
      backButton: {
        url: '',
        label: '',
        icon: null,
        show: true,
        content: <BackNavLink overrideHistory to={DashboardUrl} />
      },
      profile: {
        url: AccountProfileUrl,
        label: isMotus ? 'Profile Settings' : 'Profile',
        icon: 'person',
        isExternal: isMotus,
        show: true
      },
      perks: {
        url: AccountPerksUrl,
        label: 'Motus Perk',
        icon: null,
        motusIcon: 'percentage',
        isExternal: true,
        show: isMotus
      },
      team: {
        url: AccountTeamUrl,
        label: 'Team',
        icon: 'team',
        show: hasTeam && !isMotus
      },
      premium: {
        url: AccountPremiumUrl,
        label: 'Manage Plan',
        icon: 'premium',
        show: !hasTeam && hasSubscription && !isMotus
      },
      purposes: {
        url: AccountPurposesUrl,
        label: 'Purposes',
        icon: 'briefcase',
        show: !isMotus
      },
      taxCenter: {
        url: AccountTaxCenterUrl,
        label: 'Tax Center',
        icon: 'tax',
        show: !hasTeam && !isMotus
      },
      reimbursementsDivider: {
        url: '',
        label: '',
        icon: null,
        show:
          ENABLE_EVERLANCE_PAYMENTS &&
          !!currentUser?.team_has_everpay_enabled &&
          open,
        content: <span>REIMBURSEMENTS</span>
      },
      directDeposit: {
        url: AccountDirectDepositUrl,
        label: isMotus ? 'Direct Deposit' : 'Direct deposit',
        icon: 'wallet-with-bill',
        isExternal: isMotus,
        show:
          ENABLE_EVERLANCE_PAYMENTS && !!currentUser?.team_has_everpay_enabled
      },
      complianceDivider: {
        url: '',
        label: '',
        icon: null,
        show: isMotus && open,
        content: <span>Compliance</span>
      },
      insurance: {
        url: AccountInsuranceUrl,
        label: 'Insurance',
        icon: null,
        motusIcon: 'insurance',
        isExternal: isMotus,
        show: isMotus
      },
      motusVehicle: {
        url: AccountVehiclesUrl,
        label: 'Vehicle',
        icon: null,
        motusIcon: 'car',
        isExternal: isMotus,
        show: isMotus
      },
      motusProtectDivider: {
        url: '',
        label: '',
        icon: null,
        show: hasMotusProtectItems && open,
        content: <span>Motus Protect</span>
      },
      roadSmart: DashboardItems.roadSmart,
      mvr: DashboardItems.mvr,
      transactionsDivider: {
        url: '',
        label: '',
        icon: null,
        show: !ENABLE_EVERLANCE_PAYMENTS && open,
        content: <span>TRANSACTIONS</span>
      },
      bankCards: {
        url: AccountBankCardUrl,
        label: 'Banks & Cards',
        icon: 'cards',
        show: !ENABLE_EVERLANCE_PAYMENTS && showBankCards
      },
      rules: {
        url: AccountFavoriteTransactionsUrl,
        label: 'Rules',
        icon: 'rules',
        show: !ENABLE_EVERLANCE_PAYMENTS
      },
      tripsDivider: {
        url: '',
        label: '',
        icon: null,
        show: open,
        content: <span>TRIPS</span>
      },
      tracker: {
        url: AccountTrackerUrl,
        label: 'Tracker Settings',
        icon: 'tracker',
        show: !isMotus
      },
      favoriteTrips: {
        url: AccountFavoriteTripsUrl,
        label: 'Favorite Trips',
        icon: 'trip',
        show: !isMotus
      },
      businessHours: {
        url: AccountWorkHoursUrl,
        label: 'Business Hours',
        show: isMotus,
        icon: null,
        motusIcon: 'clock'
      },
      favoritePlaces: {
        url: AccountFavoritePlacesUrl,
        label: isMotus ? 'Locations' : 'Favorite Places',
        icon: isMotus ? null : 'favorite-place',
        motusIcon: isMotus ? 'location' : undefined,
        show: true
      },
      vehicles: {
        url: AccountVehiclesUrl,
        label: 'Vehicles',
        icon: 'vehicle',
        show: !isMotus
      },
      workHours: {
        url: AccountWorkHoursUrl,
        label: 'Work Hours',
        show: !isMotus,
        icon: 'work'
      },
      expensesDivider: {
        url: '',
        label: '',
        icon: null,
        show: isMotus ? false : ENABLE_EVERLANCE_PAYMENTS && open,
        content: <span>EXPENSES</span>
      },
      expenseSync: {
        url: AccountBankCardUrl,
        label: 'Expense sync',
        icon: 'cards',
        show: isMotus ? false : ENABLE_EVERLANCE_PAYMENTS && showBankCards
      },
      rulesExpenses: {
        url: AccountFavoriteTransactionsUrl,
        label: 'Rules',
        icon: 'rules',
        show: isMotus ? false : ENABLE_EVERLANCE_PAYMENTS
      },
      otherDivider: {
        url: '',
        label: '',
        icon: null,
        show: isMotus ? false : open,
        content: <span>OTHER</span>
      },
      history: {
        url: AccountImporterUrl,
        label: 'Import History',
        icon: 'upload-csv',
        show: !isMotus
      }
    }
  }, [
    hasTeam,
    hasSubscription,
    ENABLE_EVERLANCE_PAYMENTS,
    currentUser?.team_has_everpay_enabled,
    open,
    showBankCards,
    isMotus,
    hasMotusProtectItems,
    DashboardItems.roadSmart,
    DashboardItems.mvr
  ])

  /* Items shown on team dashboard */
  const TeamDashboard = useMemo((): Record<string, MenuItemType> => {
    return {
      home: {
        url: TeamDashboardUrl,
        label: 'Home',
        icon: 'home',
        show: true
      },
      reimbursements: {
        url: PayPeriodsUrl,
        label: 'Reimbursements',
        icon: 'rules',
        show: isRootAdmin || isSuperAdmin
      },
      members: {
        url: TeamMembersUrl,
        label: 'Members',
        icon: 'person',
        show: true
      },
      teams: {
        url: TeamsUrl,
        label: 'Teams',
        icon: 'team',
        show: true
      },
      wallet: {
        url: TeamWalletUrl,
        label: 'Wallet',
        icon: 'wallet',
        show: ENABLE_EVERLANCE_PAYMENTS && (isRootAdmin || isSuperAdmin)
      },
      exports: {
        url: TeamDataExportUrl,
        label: 'Data Exports',
        icon: 'download-excel',
        show: true
      },
      reports: {
        url: TeamReportsUrl,
        label: 'Reports',
        icon: 'report',
        show: !isRootAdmin && !isSuperAdmin
      },
      compliance: {
        url: TeamComplianceUrl,
        label: 'Compliance',
        icon: 'clipboard',
        show: hasAccessToComplianceDashboard
      },
      summary: {
        url: TeamSummaryUrl,
        label: 'Summary',
        icon: 'summary',
        show: true
      },
      premium: {
        url: TeamPremiumUrl,
        label: 'Billing',
        icon: 'cards',
        show: isRootAdmin && (!currentPlan || currentPlan.amount > 0)
      },
      settings: {
        url: TeamProfileUrl.replaceParams({
          token_id: teams?.[0]?.token_id ?? ''
        }),
        label: 'Team Settings',
        icon: 'gear',
        show: !!teams?.[0]?.token_id
      }
    }
  }, [
    isRootAdmin,
    isSuperAdmin,
    ENABLE_EVERLANCE_PAYMENTS,
    hasAccessToComplianceDashboard,
    currentPlan,
    teams
  ])

  /* Items shown on team settings */
  const TeamSettings = useMemo((): Record<string, MenuItemType> => {
    return {
      back: {
        url: '',
        label: '',
        icon: null,
        show: true,
        content: <BackNavLink overrideHistory to={TeamDashboardUrl} />
      },
      divider: {
        url: '',
        label: '',
        icon: null,
        show: true,
        content: <span>TEAM SETTINGS</span>
      },
      profile: {
        url: TeamProfileUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Team Profile',
        icon: 'person-circle',
        show: true
      },
      preferences: {
        url: TeamPreferencesUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Account Preferences',
        icon: 'gear',
        show: true
      },
      programs: {
        url: TeamProgramsUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Programs',
        icon: 'briefcase',
        show: isCurrentTeamRoot && urlTeamTokenId === currentTeamId
      },
      rules: {
        url: TeamAutomationsUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Automations',
        icon: 'flag',
        show: (isRootAdmin || isSuperAdmin) && isCurrentTeamRoot
      },
      schedules: {
        url: SchedulesUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Schedules',
        icon: 'schedule',
        show: (isRootAdmin || isSuperAdmin) && isCurrentTeamRoot
      },
      payments: {
        url: TeamSettingsPaymentsUrl.replaceParams({
          token_id: urlTeamTokenId
        }),
        label: 'Payments',
        icon: 'wallet',
        show:
          ENABLE_EVERLANCE_PAYMENTS &&
          (isRootAdmin || isSuperAdmin) &&
          isCurrentTeamRoot
      },
      tags: {
        url: TeamTagsUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Tags',
        icon: 'tag',
        show: true
      },
      categories: {
        url: TeamCategoriesUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Categories',
        icon: 'categories',
        show: true
      },
      places: {
        url: TeamPlacesUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Team Places',
        icon: 'marker',
        show: true
      },
      integrations: {
        url: TeamIntegrationsUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Integrations',
        icon: 'integrations',
        show: hasFullPower,
        subRoutes: [TeamSingleSignOnUrl, TeamMergeUrl]
      },
      roles: {
        url: TeamRolesUrl.replaceParams({ token_id: urlTeamTokenId }),
        label: 'Roles & Permissions',
        icon: 'key',
        show: true
      }
    }
  }, [
    urlTeamTokenId,
    isCurrentTeamRoot,
    currentTeamId,
    isRootAdmin,
    isSuperAdmin,
    ENABLE_EVERLANCE_PAYMENTS,
    hasFullPower
  ])

  /* Items shown on a reviewer only account */
  const ReviewerItems = useMemo((): Record<string, MenuItemType> => {
    return {
      compliance: {
        url: TeamComplianceUrl,
        label: 'Compliance',
        icon: 'clipboard',
        show: hasAccessToComplianceDashboard
      }
    }
  }, [hasAccessToComplianceDashboard])

  /* Items shown on a program settings */
  const ProgramItems = useMemo((): Record<string, MenuItemType> => {
    return {
      back: {
        url: '',
        label: '',
        icon: null,
        show: true,
        content: (
          <BackNavLink
            overrideHistory
            to={TeamProgramsUrl.replaceParams({ token_id: urlTeamTokenId })}
          />
        )
      },
      settings: {
        url: '',
        label: '',
        icon: null,
        show: true,
        content: <span>PROGRAM SETTINGS</span>
      },
      general: {
        url: TeamProgramDetailUrl.replaceParams({
          token_id: urlTeamTokenId,
          program_id: urlProgramTokenId
        }),
        label: 'General',
        icon: 'gear',
        show: true
      },
      purposes: {
        url: TeamProgramPurposesUrl.replaceParams({
          token_id: urlTeamTokenId,
          program_id: urlProgramTokenId
        }),
        label: 'Purposes',
        icon: 'briefcase',
        show: true
      }
    }
  }, [urlProgramTokenId, urlTeamTokenId])

  const getCurrentItems = useCallback(() => {
    if (section === 'dashboard') {
      return DashboardItems
    }

    if (section === 'member-invite') {
      return TeamDashboard
    }

    if (section === 'team-dashboard') {
      if (isOnlyReviewer) return ReviewerItems
      return TeamDashboard
    }

    if (section === 'program-detail' && urlTeamTokenId && urlProgramTokenId) {
      return ProgramItems
    }

    if (section === 'team-settings' && urlTeamTokenId) {
      return TeamSettings
    }

    if (section === 'settings') {
      return PersonalSettingsItems
    }

    return DashboardItems
  }, [
    DashboardItems,
    PersonalSettingsItems,
    ProgramItems,
    ReviewerItems,
    TeamDashboard,
    TeamSettings,
    isOnlyReviewer,
    section,
    urlProgramTokenId,
    urlTeamTokenId
  ])

  return getCurrentItems()
}

export default useMenuItems
